import { useState, useCallback } from 'react';

interface ShareLinkPanelProps {
  shareUrl: string;
  partnerBName: string;
  /** 'light' = white card on dark page (WaitingPage). 'dark' = muted card inside white wrapper (OnboardingFlow success). Default: 'dark' */
  variant?: 'dark' | 'light';
}

export default function ShareLinkPanel({ shareUrl, partnerBName, variant = 'dark' }: ShareLinkPanelProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    window.umami?.track('share-link-copied');
    window.AhrefsAnalytics?.sendEvent('share-link-copied');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl]);

  // Light variant: prominent white card on dark background (e.g. WaitingPage)
  if (variant === 'light') {
    return (
      <div className="bg-white rounded-2xl shadow-xl border-2 border-accent/20 p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">
          Share this link with {partnerBName}
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          They'll use it to answer the same questions privately.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={shareUrl}
            className="flex-1 min-w-0 px-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-xl text-slate-700 select-all outline-none"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={handleCopy}
            className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-xl transition-all ${
              copied
                ? 'bg-green-500 text-white'
                : 'gradient-cta text-white'
            }`}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>
    );
  }

  // Dark variant (default): muted card, used inside white wrappers (OnboardingFlow success)
  return (
    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 sm:p-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-2">
        Share this link with {partnerBName}
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        They'll use it to answer the same questions privately.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={shareUrl}
          className="flex-1 min-w-0 px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl text-slate-700 select-all outline-none"
          onFocus={(e) => e.target.select()}
        />
        <button
          onClick={handleCopy}
          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-xl transition-all ${
            copied
              ? 'bg-green-500 text-white'
              : 'gradient-cta text-white'
          }`}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
